/**
 * Labstep
 *
 * @module prosemirror/components/NodeView
 * @desc ProseMirror NodeViews
 */

import { ErrorBoundaryContainer } from 'labstep-web/containers/ErrorBoundary';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { InlineView as ViewInline } from 'labstep-web/prosemirror/components/Inline/View';
import { CodeNodeView } from 'labstep-web/prosemirror/components/NodeView/Code';
import { ConditionsNode as ConditionsNodeView } from 'labstep-web/prosemirror/components/NodeView/Conditions';
import { DateNodeView } from 'labstep-web/prosemirror/components/NodeView/Date';
import ExperimentNodeView from 'labstep-web/prosemirror/components/NodeView/Experiment';
import { ExperimentWorkflowLinkNodeView } from 'labstep-web/prosemirror/components/NodeView/ExperimentWorkflowLink';
import { FileNodeView } from 'labstep-web/prosemirror/components/NodeView/File';
import { JupyterNotebookNodeView } from 'labstep-web/prosemirror/components/NodeView/JupyterNotebook';
import { MetadataNodeView } from 'labstep-web/prosemirror/components/NodeView/Metadata';
import { MoleculeNodeView } from 'labstep-web/prosemirror/components/NodeView/Molecule';
import { ProtocolStepNodeView } from 'labstep-web/prosemirror/components/NodeView/ProtocolStep';
import { ProtocolTableNodeView } from 'labstep-web/prosemirror/components/NodeView/ProtocolTable';
import { ProtocolTimerNodeView } from 'labstep-web/prosemirror/components/NodeView/ProtocolTimer';
import { ProtocolValueNodeView } from 'labstep-web/prosemirror/components/NodeView/ProtocolValue';
import { ReferenceNodeView } from 'labstep-web/prosemirror/components/NodeView/Reference';
import { SmartTableNodeView } from 'labstep-web/prosemirror/components/NodeView/SmartTable';
import { createReactNodeView } from 'labstep-web/prosemirror/components/ReactNodeView';

export const getNodeViews = (
  entity: Experiment | Protocol,
  handleCreatePortal: any,
  timestamp?: string,
) => {
  return [
    {
      key: 'experiment',
      component: ExperimentNodeView,
    },
    {
      key: 'experiment_value',
      component: ProtocolValueNodeView,
    },
    {
      key: 'experiment_timer',
      component: ProtocolTimerNodeView,
    },
    {
      key: 'experiment_table',
      component: ProtocolTableNodeView,
    },
    {
      key: 'experiment_step',
      component: ProtocolStepNodeView,
    },
    {
      key: 'protocol_value',
      component: ProtocolValueNodeView,
    },
    {
      key: 'protocol_timer',
      component: ProtocolTimerNodeView,
    },
    {
      key: 'protocol_table',
      component: ProtocolTableNodeView,
    },
    {
      key: 'protocol_step',
      component: ProtocolStepNodeView,
    },
    {
      key: 'file',
      component: FileNodeView,
    },
    {
      key: 'reference',
      component: ReferenceNodeView,
    },
    {
      key: 'metadata',
      component: MetadataNodeView,
    },
    {
      key: 'codemirror',
      component: CodeNodeView,
    },
    {
      key: 'jupyter_notebook',
      component: JupyterNotebookNodeView,
    },
    {
      key: 'experiment_workflow_link',
      component: ExperimentWorkflowLinkNodeView,
    },
    {
      key: 'molecule',
      component: MoleculeNodeView,
    },
    {
      key: 'conditions',
      component: ConditionsNodeView,
    },
    {
      key: 'date',
      component: DateNodeView,
    },
    {
      key: 'smart_table',
      component: SmartTableNodeView,
    },
  ].reduce(
    (result, nodeView) => ({
      ...result,
      [nodeView.key](
        node: any,
        view: any,
        getPos: any,
        decorations: any,
      ) {
        const Component = nodeView.component;
        return createReactNodeView({
          node,
          view,
          getPos,
          decorations,
          component: (props) => (
            <ErrorBoundaryContainer
              FallbackComponent={<ViewInline entity={null as any} />}
            >
              <Component
                {...props}
                entity={entity}
                timestamp={timestamp}
              />
            </ErrorBoundaryContainer>
          ),
          onCreatePortal: handleCreatePortal,
        });
      },
    }),
    {},
  );
};
